import { Alert, Loader } from '@lifebit-ai/lds'
import React, { CSSProperties, useMemo, useState } from 'react'

import { useFileContentsQuery } from 'hooks/useFileContentsQuery'

import * as styles from './FileView.styles'
import { StyledFixedSizeList } from './FileView.styles'

const VALID_JSON_CONVERTIBLE_FILE_EXTENSIONS = ['json', 'ipynb']

interface FileViewProps {
  fileExtension: string
  url: string
}

const MAX_NUMBER_OF_LINES_ALLOWED = 1100000

export const FileView: React.FC<FileViewProps> = ({ fileExtension, url }) => {
  const { data, isLoading, progress, isError } = useFileContentsQuery(url)
  const [areLinesHidden, setAreLinesHidden] = useState(false)

  const arrayOfLines = useMemo(() => {
    if (!data) return undefined
    let dataAsArr = []

    // Files that wont throw an error when stringified as JSON
    if (VALID_JSON_CONVERTIBLE_FILE_EXTENSIONS.includes(fileExtension)) {
      dataAsArr = JSON.stringify(data, null, 2).split('\n')
      setAreLinesHidden(dataAsArr.length > MAX_NUMBER_OF_LINES_ALLOWED)
      return dataAsArr.filter((_i, index) => index < MAX_NUMBER_OF_LINES_ALLOWED)
    } else {
      dataAsArr = data.split('\n')
      setAreLinesHidden(dataAsArr.length > MAX_NUMBER_OF_LINES_ALLOWED)
      return dataAsArr.filter((_i, index) => index < MAX_NUMBER_OF_LINES_ALLOWED).map(i => i.trim())
    }
  }, [data, fileExtension])

  if (isError) {
    return (
      <styles.EmptyWrapper>
        <Alert type="danger">Error loading file contents.</Alert>
      </styles.EmptyWrapper>
    )
  }

  if (isLoading || !arrayOfLines) {
    return (
      <styles.EmptyWrapper>
        <Loader text={`Loading file contents${progress ? ` (${progress}%)...` : ''}`} />
      </styles.EmptyWrapper>
    )
  }

  if (!arrayOfLines.length) {
    return <styles.EmptyWrapper>No contents</styles.EmptyWrapper>
  }

  const Row = ({ index, style }: { index: number; style?: CSSProperties }) => {
    return (
      <pre style={style}>
        <styles.Number>{index + 1}</styles.Number>
        {arrayOfLines[index]}
      </pre>
    )
  }

  return (
    <styles.Container>
      {areLinesHidden ? (
        <Alert type="warning">Due to the size of this file you're only previewing a part of it.</Alert>
      ) : null}
      <styles.Wrapper>
        <StyledFixedSizeList height={600} itemCount={arrayOfLines.length} width="100%" itemSize={18}>
          {Row}
        </StyledFixedSizeList>
      </styles.Wrapper>
    </styles.Container>
  )
}

export default FileView
